import { api } from './api';

export interface Recommendation {
  id: string;
  traveler_id: string;
  consultation_id: string;
  traveler_name: string;
  consultation_number: string;
  created_at: string;
  departure_date?: string;
  return_date?: string;
  overall_review_level: 'LOW' | 'MEDIUM' | 'HIGH';
  final_recommendations: {
    vaccines: Array<{
      dose: string;
      flags: string[];
      route: string;
      status: string;
      timing: string;
      reasoning: string;
      protocol_id: string;
      vaccine_name: string;
      therapy_class: string;
      series_schedule: string;
      counseling_points: string[];
    }>;
    medications: Array<{
      dose: string;
      flags: string[];
      route: string;
      status: string;
      timing: string;
      quantity: number;
      frequency: string;
      reasoning: string;
      protocol_id: string;
      medication_name: string;
      therapy_class: string;
      counseling_points: string[];
      quantity_reasoning: string;
    }>;
    not_recommended: Array<{
      name: string;
      reason: string;
      therapy_class: string;
    }>;
  };
  thought_process: any;
  validation_passed: boolean;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  llm_provider: string;
  llm_model_id: string;
  system_prompt?: string;
  user_prompt?: string;
}

export interface RecommendationFilters {
  traveler_name?: string;
  consultation_number?: string;
  review_level?: string;
  date_from?: string;
  date_to?: string;
  validation_passed?: boolean;
}

export interface RecommendationStats {
  total_recommendations: number;
  unique_travelers: number;
  avg_input_tokens: number;
  avg_output_tokens: number;
  validation_success_rate: number;
  high_risk_cases: number;
  provider_breakdown: Array<{
    provider: string;
    count: number;
    avg_tokens: number;
  }>;
}

export interface FormSection {
  section: string;
  display_name: string;
  data: Record<string, unknown>;
  completed: boolean;
  completed_at: string | null;
}

export interface PatientFormData {
  recommendation_id: string;
  consultation_id: string;
  consultation_number: string;
  traveler: {
    id: string;
    name: string;
    email: string;
    is_minor: boolean;
    date_of_birth: string | null;
    relationship: string;
    form_status: string;
  };
  travel_summary: {
    countries: string[];
    cities: string[];
    destinations: Array<{
      country: string;
      city: string;
      region: string;
      days: number;
      activities: string[];
      order: number;
    }>;
    departure_date: string | null;
    return_date: string | null;
    total_days: number | null;
    days_until_departure: number | null;
    vaccination_timeline: {
      urgent?: boolean;
      standard?: boolean;
      ideal?: boolean;
      days_available?: number;
    };
    total_destinations: number;
  };
  selected_pharmacy: {
    id: string;
    name: string;
    address: string;
    phone: string;
    email: string;
  } | null;
  form_sections: FormSection[];
}

export const recommendationsApi = {
  // Get recent recommendations with pagination
  getRecommendations: async (
    page: number = 1, 
    limit: number = 20, 
    filters?: RecommendationFilters
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    
    // Add filters if they exist
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    
    try {
      const response = await api.get<{
        recommendations: Recommendation[];
        total: number;
        page: number;
        limit: number;
      }>(`/api/v1/admin/recommendations?${params}`);
      
      // The API returns a proper paginated response structure
      return {
        recommendations: Array.isArray(response.data.recommendations) ? response.data.recommendations : [],
        total: response.data.total || 0,
        page: response.data.page || page,
        limit: response.data.limit || limit
      };
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  },

  // Get specific recommendation details
  getRecommendation: async (id: string) => {
    const response = await api.get<{
      recommendation: Recommendation;
      requested_by: string;
    }>(`/api/v1/admin/recommendations/${id}`);
    return response.data.recommendation;
  },

  // Get recommendations for specific traveler
  getTravelerRecommendations: async (travelerId: string) => {
    const response = await api.get<Recommendation[]>(`/api/v1/admin/recommendations/traveler/${travelerId}`);
    return response.data;
  },

  // Get recommendation analytics
  getStats: async () => {
    const response = await api.get<RecommendationStats>('/api/v1/admin/recommendations/stats');
    return response.data;
  },

  // Search recommendations
  searchRecommendations: async (query: string) => {
    const response = await api.get<Recommendation[]>(`/api/v1/admin/recommendations/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Get patient form data for a recommendation
  getRecommendationFormData: async (id: string): Promise<PatientFormData> => {
    const response = await api.get<PatientFormData>(`/api/v1/admin/recommendations/${id}/form-data`);
    return response.data;
  }
};